import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X, CheckCircle2 } from 'lucide-react';
import { hrRequestApi } from '../../api/hr-requests';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { ReturnAction } from '../../components/ReturnAction';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { HR_REQUEST_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';

const TYPE_LABELS: Record<string, string> = {
  confirmation_letter: 'Employment Confirmation Letter', salary_confirmation: 'Salary Confirmation',
  id_card_replacement: 'ID Card Replacement', contract_amendment: 'Contract Amendment',
  profile_update: 'Profile Update', training: 'Training', other: 'Other',
};

export function HrRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('hr_requests.manage');

  const requestQuery = useQuery({ queryKey: ['hr-request', requestId], queryFn: () => hrRequestApi.getById(requestId) });
  const docsQuery = useQuery({ queryKey: ['hr-request-documents', requestId], queryFn: () => genericDocumentApi.listFor('hr_request', requestId) });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['hr-request', requestId] });
  const resolveMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => hrRequestApi.resolve(requestId, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const completeMutation = useMutation({
    mutationFn: () => hrRequestApi.complete(requestId),
    onSuccess: () => { showSuccess('Marked completed.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  if (!requestQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const r = requestQuery.data.data;

  return (
    <div className="page">
      <Link to="/hr/requests">&larr; Back to HR Requests</Link>
      <RecordHeader
        title={`HR Request - ${r.employee_name ?? ''}`}
        entityType="hr_request"
        entityId={requestId}
        fields={[{ label: 'Request type', value: TYPE_LABELS[r.request_type] ?? r.request_type }]}
        actions={
          <span className="row-actions">
            {(r.status === 'submitted' || r.status === 'under_review') && (
              <>
                <button className="btn-primary" onClick={() => resolveMutation.mutate('approved')} disabled={resolveMutation.isPending}><Check size={16} className="inline-icon" /> Approve</button>
                <button className="button-danger" onClick={() => resolveMutation.mutate('rejected')} disabled={resolveMutation.isPending}><X size={16} className="inline-icon" /> Reject</button>
                <ReturnAction onReturn={(comment) => hrRequestApi.return(requestId, comment)} queryKeysToInvalidate={[['hr-request', requestId]]} />
              </>
            )}
            {r.status === 'approved' && <button className="btn-primary" onClick={() => completeMutation.mutate()} disabled={completeMutation.isPending}><CheckCircle2 size={16} className="inline-icon" /> Mark Completed</button>}
          </span>
        }
        readOnly={!canManage}
      />
      <p className="muted">Status: <StatusBadge status={r.status} /></p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  {r.details && (<><h3>Details</h3><p>{r.details}</p></>)}
                  {r.return_comment && <p className="muted">Return comment: {r.return_comment}</p>}
                  {r.resolution_notes && <p className="muted">Resolution notes: {r.resolution_notes}</p>}
                  {r.proposed_changes && (
                    <>
                      <h3>Proposed Profile Changes</h3>
                      <table className="data-table">
                        <thead><tr><th>Field</th><th>Current</th><th>Proposed</th></tr></thead>
                        <tbody>
                          {Object.entries(r.proposed_changes).map(([field, proposedValue]) => (
                            <tr key={field}>
                              <td>{field}</td>
                              <td>{r.current_values?.[field] ?? '-'}</td>
                              <td>{proposedValue ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </section>
                <CommentsThread entityType="hr_request" entityId={requestId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('hr_request', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('hr_request', requestId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('hr_request', documentId)}
                queryKeyToInvalidate={['hr-request-documents', requestId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="hr_request" entityId={requestId} actionStageMap={HR_REQUEST_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
